import * as zod from 'zod';

import type { BaseFeatureFlagDefinition, Section } from './featureFlags';

export type InternalFeatureFlagDefinitionSchema = BaseFeatureFlagDefinition & {
  section?: Section;
  // Optional here, required on development flags. The control panel renders it
  // next to the flag's age and searches it, so it survives a flag graduating
  // from the development schema into this one.
  owner?: string;
};

// Internal testing feature flags
export const InternalFeatureFlagDataZod = zod.object({
  adHocFullSpeed: zod.boolean().default(false),
  testingSafetyLimits: zod.boolean().default(false),
  sanitizeBlackboxCan: zod.boolean().default(false),
  enableFoxgloveServer: zod.boolean().default(false),
  foxgloveViaMetricsBot: zod.boolean().default(false),
  disableEstopHealthCheck: zod.boolean().default(false),
  dualModeCollisionDisabled: zod.boolean().default(false),
  locateStepTextPrompt: zod.boolean().default(true),
  showStackTraceInErrorBoundary: zod.boolean().default(false),
  visualizeTooltipPath: zod.boolean().default(false),
  visualizeGripperCollisionBox: zod.boolean().default(false),
  visualizeCollisionSpheres: zod.boolean().default(false),
  doNotResetPositionOnTrajectoryReset: zod.boolean().default(true),
  enableJointStoppingAccelerations: zod.boolean().default(false),
  disableErrorOnInvalidTrajectory: zod.boolean().default(false),
  thorPedestalMount: zod.boolean().default(true),
  treatCanInvalidAsWarning: zod.boolean().default(false),
  movementAnalyzer: zod.boolean().default(false),
  disableMotionPlanCache: zod.boolean().default(false),
  botmanEventLoopProfiler: zod.boolean().default(false),
  botmanHeapAllocProfiler: zod.boolean().default(false),
  botmanTraceGcNvp: zod.boolean().default(false),
  botmanTraceGcObjectStats: zod.boolean().default(false),
  botmanRosActionProfiler: zod.boolean().default(false),
  ro1LegacySupport: zod.boolean().default(false),
  controlBoxResourceMetrics: zod.boolean().default(true),
  reportActualJointPositions: zod.boolean().default(true),
  preferFileCalibrationOverFlash: zod.boolean().default(false),
  newSolenoid: zod.boolean().default(true),
  useTeachPendant: zod.boolean().default(true),
  complianceMode: zod.boolean().default(false),
  // On, matching the Go and C++ tiers, which already default it on. This
  // switches updater-bot's daemon set from camera-bot to camera-bot-cpp. A
  // Locate/Classify/Capture step saved against the wrist camera carries no
  // cameraEquipmentId and fails until an operator creates Camera equipment for
  // the wrist camera and re-points the step; there is deliberately no backfill,
  // because the device id is the camera's serial and nothing off-robot knows it.
  // Bot migration 0087 deletes the WristCamera equipment rows outright — this
  // mode has no service that can drive one.
  //
  // Sticky per robot: FeatureFlagsService.materializeDefaults() writes every
  // default-true key it finds missing into feature_flags.json and never removes
  // one, so the first boot on a build carrying this default pins `true` on the
  // box. Flipping this line back, or rolling onto an older build, leaves that
  // file saying `true`. To put a box back on the legacy camera-bot, turn the
  // flag off explicitly (control panel, or the file) — an explicit `false` is
  // persisted and survives both.
  useCppCameraBot: zod.boolean().default(true),
  checkpointBasedLocate: zod.boolean().default(false),
  autoCameraCalibration: zod.boolean().default(false),
  enableSkills: zod.boolean().default(true),
  demonstrationRecording: zod.boolean().default(false),
  zmqPoseMigration: zod.boolean().default(false),
  zmqBagRecorder: zod.boolean().default(true),
  enableRoboflowInference: zod.boolean().default(true),
  roboflowUpload: zod.boolean().default(true),
  enableInspect: zod.boolean().default(true),
  nonPlanarLocate: zod.boolean().default(true),
  visionBotZmqMigration: zod.boolean().default(true),
  dinoFastLocate: zod.boolean().default(true),
  goldenRoutineLibrary: zod.boolean().default(true),
  goldenApplicationGuides: zod.boolean().default(true),
});

export type InternalFeatureFlagData = zod.infer<
  typeof InternalFeatureFlagDataZod
>;
export type InternalFeatureFlagKey = keyof InternalFeatureFlagData;

export const internalFeatureFlagDefinitions: Record<
  InternalFeatureFlagKey,
  InternalFeatureFlagDefinitionSchema
> = {
  adHocFullSpeed: {
    requiresRestart: false,
    title: 'Full Speed Adhoc Movements',
    dateAdded: '2022-11-18',
    section: 'movement',
    allowSingleUser: true,
    description: 'Enable full speed adhoc movements',
  },
  testingSafetyLimits: {
    requiresRestart: false,
    title: 'Testing Safety Limits',
    dateAdded: '2026-02-10',
    description: 'Unlock safety limits for RO3 testing',
    section: 'safety',
    allowSingleUser: false,
  },
  sanitizeBlackboxCan: {
    title: 'Sanitize Blackbox CAN',
    dateAdded: '2025-09-09',
    section: 'infra',
    allowSingleUser: false,
    description: 'Replace NaN values in Blackbox CAN payloads before logging',
  },
  enableFoxgloveServer: {
    title: 'Enable Foxglove Server',
    dateAdded: '2025-07-16',
    section: 'infra',
    allowSingleUser: false,
    description: 'Enable the Foxglove server',
  },
  foxgloveViaMetricsBot: {
    title: 'Foxglove via metrics-bot',
    dateAdded: '2026-08-14',
    section: 'infra',
    allowSingleUser: false,
    description:
      'metrics-bot serves the live Foxglove WebSocket (8765) from its blackbox streams instead of botman. Live topics take their blackbox names (saved layouts need updating) and non-CAN botman feeds (simulated encoder feedback, disturbance/expected-torque, equipment state) are not served yet. Restart botman and metrics-bot.',
  },
  disableEstopHealthCheck: {
    title: 'Disable pre-unbrake Estop check',
    dateAdded: '2024-02-15',
    section: 'safety',
    allowSingleUser: false,
  },
  dualModeCollisionDisabled: {
    title: 'Disable IMU acceleration threshold detection',
    dateAdded: '2025-02-17',
    section: 'safety',
    allowSingleUser: false,
  },
  locateStepTextPrompt: {
    title: 'Locate Step Text Prompting',
    dateAdded: '2025-05-02',
    section: 'vision',
    allowSingleUser: false,
    description:
      'Adds a text prompt to the locate step for recognition of objects in 3D space',
  },
  showStackTraceInErrorBoundary: {
    requiresRestart: false,
    title: 'Show Stack Traces in Error Boundary',
    dateAdded: '2025-11-14',
    section: 'infra',
    allowSingleUser: true,
    description:
      'Display error stack traces in a collapsible section when errors are caught by the error boundary',
  },
  visualizeTooltipPath: {
    requiresRestart: false,
    title: 'Visualize Tooltip Path',
    dateAdded: '2025-11-17',
    section: 'movement',
    allowSingleUser: true,
    description: 'Visualize the path of the tooltip in the visualizer',
  },
  visualizeGripperCollisionBox: {
    requiresRestart: false,
    title: 'Visualize Gripper Collision Box',
    dateAdded: '2026-08-06',
    section: 'movement',
    allowSingleUser: true,
    description:
      "Draw the equipped gripper's motion-planning collision boxes in the visualizer. Read-only: reflects the collision boxes already configured on the equipment, and does not affect collision checking or motion planning.",
  },
  visualizeCollisionSpheres: {
    requiresRestart: false,
    title: 'Visualize Collision Spheres',
    dateAdded: '2026-08-14',
    section: 'movement',
    allowSingleUser: true,
    description:
      "Render the self-collision sphere model as translucent spheres on the arm in the visualizer, for debugging the robot's self-collision avoidance",
  },
  doNotResetPositionOnTrajectoryReset: {
    title: 'Reset Only Velocity on Trajectory Reset',
    dateAdded: '2025-12-05',
    section: 'movement',
    allowSingleUser: false,
    description: 'Reset only the velocity on trajectory reset',
  },
  enableJointStoppingAccelerations: {
    requiresRestart: false,
    title: 'Enable Joint Stopping Accelerations',
    dateAdded: '2025-11-18',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Show per-joint stopping acceleration controls in safety settings. The backend always uses jointStoppingAccelerations; this flag only controls UI visibility.',
  },
  disableErrorOnInvalidTrajectory: {
    title: 'Disable Error on Invalid Trajectory',
    dateAdded: '2025-12-05',
    section: 'safety',
    allowSingleUser: false,
    description: 'Disable error on invalid trajectory',
  },
  thorPedestalMount: {
    title: 'Thor Pedestal Mount',
    dateAdded: '2025-12-10',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Enable pedestal mount mode for Thor robot models (disables floor collision boundary)',
  },
  treatCanInvalidAsWarning: {
    title: 'Treat CAN Invalid as Warning',
    dateAdded: '2025-12-11',
    section: 'safety',
    allowSingleUser: false,
    description:
      'Treat CAN Invalid Message ResultType as warning level instead of error level',
  },
  movementAnalyzer: {
    title: 'Movement Analyzer',
    dateAdded: '2026-03-03',
    section: 'movement',
    allowSingleUser: false,
    description: 'Enable movement analyzer functionality in kalibrate-bot',
  },
  disableMotionPlanCache: {
    title: 'Disable Motion Plan Cache',
    dateAdded: '2026-04-07',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Completely disable the motion plan cache. Every motion plan request will be computed fresh. Useful for debugging motion planning issues.',
  },
  botmanEventLoopProfiler: {
    title: 'Botman Event Loop Profiler',
    dateAdded: '2026-05-20',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enables a rolling event-loop CPU profiler that saves up to 5s of pre-spike samples to /tmp/botman-profiles/ when loop lag exceeds 200ms in a 1s window (one save per 60s, keeps 10 most recent). Use to diagnose heartbeat drift.',
  },
  botmanHeapAllocProfiler: {
    title: 'Botman Heap Allocation Profiler',
    dateAdded: '2026-05-20',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enables a rolling V8 heap allocation sampler (512KB interval) that saves up to 5s of pre-spike samples to /tmp/botman-heap-profiles/ when loop lag exceeds 200ms in a 1s window (one save per 60s, keeps 10 most recent). Output is a .heapprofile loadable in Chrome DevTools (Memory → Load profile) showing which call sites allocated the bytes. Use to diagnose allocation-driven GC pressure or leaks.',
  },
  botmanTraceGcNvp: {
    title: 'Botman --trace-gc-nvp',
    dateAdded: '2026-05-20',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Adds --trace-gc-nvp to botman startup (requires restart). Writes one parseable line per GC with timing + heap stats.',
  },
  botmanTraceGcObjectStats: {
    title: 'Botman --trace-gc-object-stats',
    dateAdded: '2026-05-20',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Adds --trace-gc-object-stats to botman startup (requires restart). Emits a per-type breakdown (JS_OBJECT_TYPE, JS_FUNCTION_TYPE, STRING_TYPE, ...) at each GC. Adds ~100-300 lines per GC and a single-digit % cost to each pause — fine for an investigation window, not a long-term default.',
  },
  botmanRosActionProfiler: {
    title: 'Botman ROS Action Profiler',
    dateAdded: '2026-05-28',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Logs per-phase timings (createGoal / getClient / goal acceptance / execution) for each ROS action goal in ExecuteAction/BaseAction. Lightweight (hrtime + one log line per completed goal). Use to locate where ROS action latency originates when diagnosing heartbeat drift.',
  },
  ro1LegacySupport: {
    title: 'RO1 Legacy Support',
    dateAdded: '2026-06-30',
    section: 'movement',
    allowSingleUser: false,
    description:
      'Legacy support for RO1 arms on older firmware: disables cascaded motor control and skips the unbrake motion-verification wiggle (unbrake distance 0). Current motor-board firmware ignores the cascaded-control bit (cascaded control is unconditional and RO1 targets are no longer built), so the bit only affects arms still on older firmware. NOTE: also enable "Disable Dynamic Hardware Config" on these robots.',
  },
  controlBoxResourceMetrics: {
    title: 'Control Box Resource Metrics',
    dateAdded: '2026-03-09',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable Prometheus + cAdvisor monitoring stack for per-container resource metrics',
  },
  reportActualJointPositions: {
    title: 'Report Actual Joint Positions',
    dateAdded: '2026-04-21',
    section: 'movement',
    allowSingleUser: false,
    description:
      'arm-control-bot reports measured encoder positions instead of commanded setpoints in its state stream. Required for routine-runner collision detection to see simulator positionDeltas and real-hardware drift; exposes encoder noise to the collision loop.',
  },
  preferFileCalibrationOverFlash: {
    title: 'Prefer File Calibration Over Flash',
    dateAdded: '2026-07-20',
    section: 'infra',
    allowSingleUser: false,
    requiresRestart: false,
    description:
      'When true, the calibration resolver tries the override file (kinematic_calibration.json) first and falls back to flash-stored calibration. When false (default), flash is tried first and the override file is the fallback.',
  },
  newSolenoid: {
    title: 'New Solenoid (Rev C)',
    dateAdded: '2026-07-22',
    section: 'safety',
    allowSingleUser: false,
    description:
      'This robot has Rev C brake solenoids fitted (Thor). Written to each ' +
      'joint’s flash metadata at the next firmware install; motor-board ' +
      'firmware then drives the brake with Rev C duty cycles. Only enable ' +
      'after EVERY joint’s solenoid has been physically swapped to Rev C ' +
      '— Rev C drive on a Rev B solenoid can brown out the motor board.',
  },
  useTeachPendant: {
    title: 'Use Teach Pendant 3PE',
    dateAdded: '2025-12-18',
    description: "Enable use of the teach pendant's 3PE safety stop",
    section: 'safety',
    allowSingleUser: false,
  },
  complianceMode: {
    title: 'Compliance mode',
    dateAdded: '2025-07-17',
    description:
      'Run compliance controller as the pose-based real-time controller',
    section: 'AI',
    owner: 'jacob',
  },
  useCppCameraBot: {
    title: 'Use C++ Camera Bot',
    dateAdded: '2025-09-22',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Run camera-bot-cpp instead of the legacy camera-bot. On by default. The built-in wrist camera is removed from the equipment manager, so steps saved against it need Camera equipment created for it and the step re-pointed; turn this off to go back.',
    owner: 'cnr',
  },
  checkpointBasedLocate: {
    title: 'Checkpoint-based Locate',
    dateAdded: '2025-09-11',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable checkpoint-based locate functionality for improved object detection and positioning',
    owner: 'kavan',
  },
  autoCameraCalibration: {
    title: 'Automatic Camera Calibration',
    dateAdded: '2026-07-30',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable the streaming "calibration mode" for external camera intrinsics/extrinsics (auto-capture + live coverage).',
    owner: 'kavan',
  },
  enableSkills: {
    title: 'Enable Skills',
    dateAdded: '2026-06-09',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable the self-serve Skills experience (skill wizard, skill list, and skill steps)',
    owner: 'kavan',
  },
  demonstrationRecording: {
    title: 'Demonstration Recording',
    dateAdded: '2026-05-28',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Enable Demonstration Skill: record a hand-guided trajectory once and replay it later, pinned to a chosen anchor frame.',
    owner: 'jacob',
  },
  zmqPoseMigration: {
    title: 'ZMQ Pose Migration',
    dateAdded: '2025-06-30',
    section: 'infra',
    allowSingleUser: false,
    description: 'Enable migration of pose messaging to ZMQ',
    owner: 'jacob',
  },
  zmqBagRecorder: {
    title: 'ZMQ Bag Recorder & Metrics',
    dateAdded: '2026-04-10',
    section: 'infra',
    allowSingleUser: false,
    description:
      'Enable ZMQ bag recorder (MCAP recording) and messenger metrics counters. Default on; disable to turn off observability overhead.',
    owner: 'jacob',
  },
  enableRoboflowInference: {
    title: 'Roboflow Inference',
    dateAdded: '2026-07-21',
    section: 'vision',
    allowSingleUser: false,
    description:
      'Show the Roboflow model source when configuring a locate skill, so a Roboflow-hosted model can run on-robot via the roboflow-inference-bot container. For a locate skill this is UI-only: the container runs on any amd64 robot whose CPU clears the processor floor, independent of this flag. For an Inspect skill it also stops inference.',
    owner: 'raphael',
  },
  /**
   * KNOWN DEBT: a feature flag is the wrong home for this in the long run.
   *
   * Whether a robot sends frames to the customer's Roboflow account is a
   * choice the customer should be making themselves, which puts it among the
   * settings an operator can reach rather than among the internal flags. It is
   * here because the surface that would own it does not exist yet. Anyone
   * building that surface: the value is read at container launch
   * (`ACTIVE_LEARNING_ENABLED` in
   * `roboflow_inference_bot.go`), so wherever it moves has to restart the
   * container for a change to mean anything.
   */
  roboflowUpload: {
    title: 'Roboflow Upload',
    dateAdded: '2026-09-09',
    section: 'vision',
    allowSingleUser: false,
    description:
      "Let the inference container send camera frames to the customer's Roboflow account through a workflow's Dataset Upload block (sampled frames for retraining) or Vision Events block (images and predictions for Roboflow Vision Events). On by default. Turn it off to stop those two blocks. Other blocks that call Roboflow-hosted services, such as Email Notification with Roboflow's managed key, can still send images. It applies to the whole robot, because it is a container environment variable and one container serves every skill. A skill on a remote GPU runner follows that runner's flag. Does NOT affect local or S3/FTP capture: a routine's own records are written either way.",
  },
  enableInspect: {
    title: 'Inspect Skill',
    dateAdded: '2026-09-03',
    section: 'vision',
    allowSingleUser: false,
    description:
      'Offer the Inspect skill type, which runs a Roboflow detection workflow at a pose and publishes a count per defect class. Separate from Roboflow Inference so Inspect can be turned on or off without changing what a locate skill offers; Inspect additionally needs that flag, since Roboflow is the only model source it has.',
  },
  nonPlanarLocate: {
    title: 'Non-Planar Locate',
    dateAdded: '2026-03-12',
    section: 'vision',
    allowSingleUser: false,
    description: 'Enable non-planar plane estimation in the Locate step.',
    owner: 'derek',
  },
  visionBotZmqMigration: {
    title: 'Vision Bot ZMQ Migration',
    dateAdded: '2026-04-06',
    section: 'vision',
    allowSingleUser: false,
    description:
      'Route all vision-bot RPCs through ZMQ instead of Thrift. Requires GPUTaskRunner.',
    owner: 'derek',
  },
  dinoFastLocate: {
    requiresRestart: false,
    title: 'ClickFind AI Locate',
    dateAdded: '2026-05-14',
    section: 'vision',
    allowSingleUser: true,
    description:
      'Enable ClickFind AI locate method (visual similarity to a clicked target region using patch embeddings). Requires Vision Bot ZMQ Migration — the embedding RPCs this method needs are not available over Thrift, so leaving that flag off makes ClickFind throw at runtime.',
    owner: 'kavan',
  },
  goldenRoutineLibrary: {
    title: 'Golden Routine Library',
    dateAdded: '2026-08-18',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Serve the Routine Agent the full curated example-routine library. ' +
      'When off, the agent sees only the embedded move-sequence example. ' +
      'agent-bot reads this once at boot, so a toggle takes effect on the ' +
      'next agent-bot restart.',
    owner: 'rox',
  },
  goldenApplicationGuides: {
    title: 'Golden Application Guides',
    dateAdded: '2026-08-18',
    section: 'AI',
    allowSingleUser: false,
    description:
      'Serve per-application authoring guides to the Routine Agent through ' +
      'its get_application_guide tool. Independent of Golden Routine ' +
      'Library so the guidance and example surfaces can be compared ' +
      'separately. agent-bot reads this once at boot, so a toggle takes ' +
      'effect on the next agent-bot restart.',
    owner: 'rox',
  },
};

type GroupedKeys = { section: Section; keys: InternalFeatureFlagKey[] }[];

export const internalTestingFeatureFlags: GroupedKeys = (() => {
  const grouped: GroupedKeys = [];

  for (const keyStr of Object.keys(internalFeatureFlagDefinitions)) {
    const key = keyStr as InternalFeatureFlagKey;
    let { section } = internalFeatureFlagDefinitions[key];

    if (!section) {
      section = 'other';
    }

    const group = grouped.find((g) => g.section === section);

    if (group) {
      group.keys.push(key);
    } else {
      grouped.push({ section, keys: [key] });
    }
  }

  // sort by section
  return grouped.sort((a, b) => {
    if (a.section === 'other') {
      return 1;
    }

    if (b.section === 'other') {
      return -1;
    }

    return a.section.localeCompare(b.section);
  });
})();
